import React, { useCallback, useEffect, useState } from 'react';
import { Button } from '../components/ui/Button';
import { Printer, MapPin, Phone, Instagram, Check } from 'lucide-react';
type PrintMode = 'front' | 'back' | 'both' | 'single' | null;
export function RaffleVoucherPage() {
  const [printMode, setPrintMode] = useState<PrintMode>(null);
  const handlePrint = useCallback((mode: PrintMode) => {
    setPrintMode(mode);
  }, []);
  useEffect(() => {
    if (printMode) {
      const timeout = setTimeout(() => {
        window.print();
        setPrintMode(null);
      }, 100);
      return () => clearTimeout(timeout);
    }
  }, [printMode]);
  const printClass = printMode ? `print-mode-${printMode}` : '';
  return <div className={`min-h-screen bg-gray-100 p-4 sm:p-8 print:p-0 print:bg-white flex flex-col items-center ${printClass}`} data-id="element-3179" data-mp="2ehz2j3fvpam4">
      {/* Controls - Hidden when printing */}
      <div className="max-w-[210mm] w-full mb-8 no-print" data-id="element-3180" data-mp="3pwclbtvryzva">
        <div className="flex flex-col sm:flex-row gap-4 items-center justify-between mb-6" data-id="element-3181" data-mp="3purqs49gngxf">
          <div data-id="element-3182" data-mp="32rufecxl1u58">
            <h1 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-3183" data-mp="32q9ulf12lpkk">
              Raffle Gift Voucher
            </h1>
            <p className="text-gray-600 text-sm" data-id="element-3184" data-mp="2su6jhjo8qglh">
              Print each side separately as A5, or both together on A4
              Landscape.
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-3 items-center" data-id="element-3185" data-mp="2nxg2mu4m3gz0">
          <Button onClick={() => handlePrint('front')} className="gap-2 shadow-lg" data-id="element-3186" data-mp="2ju9zhvcsq5wr">
            <Printer className="w-4 h-4" data-id="element-3187" data-mp="2f1gwpd0w17nb" />
            Print Front Side
          </Button>
          <Button onClick={() => handlePrint('back')} className="gap-2 shadow-lg" data-id="element-3188" data-mp="2a46v6z0855j4">
            <Printer className="w-4 h-4" data-id="element-3189" data-mp="252hn9s8at7z9" />
            Print Back Side
          </Button>
          <Button onClick={() => handlePrint('both')} variant="outline" className="gap-2" data-id="element-3190" data-mp="204pruo8g33qb">
            <Printer className="w-4 h-4" data-id="element-3191" data-mp="1lj26x0uf3v6e" />
            Print Both (A4 Landscape)
          </Button>
          <Button onClick={() => handlePrint('single')} variant="outline" className="gap-2" data-id="element-3192" data-mp="1qhnc7eoj151p">
            <Printer className="w-4 h-4" data-id="element-3193" data-mp="1cjfe33fr7xoy" />
            Print All on One Page (A4)
          </Button>
        </div>
        <p className="text-xs text-gray-500 mt-3" data-id="element-3194" data-mp="1hhh11zgawh5p">
          <strong data-id="element-3195" data-mp="2569plaulfmqa">Front / Back:</strong> Set paper to{' '}
          <strong data-id="element-3196" data-mp="258w5bxrn18jl">A5 Portrait</strong>, margins <strong data-id="element-3197" data-mp="298wk5jinflr6">None</strong>, enable{' '}
          <strong data-id="element-3198" data-mp="297v4t2ulod3n">Background graphics</strong>.<br data-id="element-3199" data-mp="1vgztzl0jjlxf" />
          <strong data-id="element-3200" data-mp="1vhhzl893fci0">Both:</strong> Set paper to <strong data-id="element-3201" data-mp="1vg6tv2w37uo9">A4 Landscape</strong>,
          margins <strong data-id="element-3202" data-mp="20g30l9w6607i">None</strong>, enable{' '}
          <strong data-id="element-3203" data-mp="20erxlcajswhg">Background graphics</strong>.
        </p>
      </div>

      {/* ═══════════════════════════════════════════ */}
      {/* FRONT SIDE CARD (Screen Preview + Print)   */}
      {/* ═══════════════════════════════════════════ */}
      <div className="no-print mb-4 text-sm font-semibold text-gray-500 uppercase tracking-wider" data-id="element-3204" data-mp="2sy71f1y1mere">
        Front Side
      </div>
      <div className="front-panel bg-white shadow-2xl overflow-hidden relative mb-10 print-panel" style={{
      WebkitPrintColorAdjust: 'exact',
      printColorAdjust: 'exact'
    } as React.CSSProperties} data-id="element-3205" data-mp="2ya41qst9yqnr">
        {/* Background Image */}
        <img src="https://images.unsplash.com/photo-1606811841689-23dfddce3e95?auto=format&fit=crop&w=1200&q=80" alt="Dentist and patient smiling" className="absolute inset-0 w-full h-full object-cover z-0" data-id="element-3206" data-mp="2jbjr0qy1m6jt" />

        {/* Dark overlay */}
        <div className="absolute inset-0 z-[1]" style={{
        background: 'linear-gradient(to bottom, rgba(0,0,0,0.50), rgba(0,0,0,0.35), rgba(0,0,0,0.55))',
        WebkitPrintColorAdjust: 'exact',
        printColorAdjust: 'exact'
      } as React.CSSProperties} data-id="element-3207" data-mp="2obo7wi2v53ak"></div>

        {/* Elegant Double Border Inset */}
        <div className="absolute inset-5 border z-[2] pointer-events-none" style={{
        borderColor: 'rgba(255,255,255,0.25)'
      }} data-id="element-3208" data-mp="2e812m8o6gc0i"></div>
        <div className="absolute inset-7 border z-[2] pointer-events-none" style={{
        borderColor: 'rgba(255,255,255,0.10)'
      }} data-id="element-3209" data-mp="2n98vucbu6mnq"></div>

        {/* Content */}
        <div className="relative z-[3] flex flex-col items-center w-full h-full justify-between py-10 px-10 text-center" data-id="element-3210" data-mp="2x755ezp4l9jq">
          {/* Top Branding */}
          <div className="flex flex-col items-center" data-id="element-3211" data-mp="31yx09mfbe1fo">
            <div className="rounded-xl p-3 mb-4" style={{
            backgroundColor: 'rgba(255,255,255,0.95)'
          }} data-id="element-3212" data-mp="36xhqct0ur4qe">
              <img src="https://cdn.magicpatterns.com/uploads/oHJPxkkc7f6GgU5uyo3LQX/ChatGPT_Image_Feb_5,_2026,_03_12_40_PM.png" alt="Dentico Clinic Logo" className="h-12 w-auto object-contain" data-id="element-3213" data-mp="3fxwgq6soba0r" />
            </div>
            <span className="text-2xl font-bold tracking-tight leading-none mb-2" style={{
            color: '#ffffff',
            textShadow: '0 1px 3px rgba(0,0,0,0.3)'
          }} data-id="element-3214" data-mp="zdkovjx1jlju">
              DenticoClinic
            </span>
            <p className="text-sm uppercase leading-tight" style={{
            color: 'rgba(255,255,255,0.85)',
            letterSpacing: '0.3em',
            textShadow: '0 1px 2px rgba(0,0,0,0.3)'
          }} data-id="element-3215" data-mp="1n098qb5m1o33">
              Private Dentistry
            </p>
          </div>

          {/* Center Content */}
          <div className="flex flex-col items-center w-full" data-id="element-3216" data-mp="26q35z4r3gvep">
            <div className="w-12 h-px mb-7" style={{
            backgroundColor: 'rgba(255,255,255,0.4)'
          }} data-id="element-3217" data-mp="21ybbij4ti5ja"></div>

            <p className="text-xs uppercase font-medium mb-5" style={{
            color: 'rgba(255,255,255,0.75)',
            letterSpacing: '0.4em',
            textShadow: '0 1px 2px rgba(0,0,0,0.3)'
          }} data-id="element-3218" data-mp="2kwmygq2zbn5d">
              Gift Voucher
            </p>

            <h2 className="text-4xl font-serif mb-8 leading-tight" style={{
            color: '#ffffff',
            textShadow: '0 1px 4px rgba(0,0,0,0.3)'
          }} data-id="element-3219" data-mp="2242a9ly8667w">
              Professional
              <br data-id="element-3220" data-mp="1t4yq0enbnkrt" />
              Teeth Whitening
            </h2>

            <div className="inline-block py-4 px-12" style={{
            borderTop: '1px solid rgba(255,255,255,0.35)',
            borderBottom: '1px solid rgba(255,255,255,0.35)'
          }} data-id="element-3221" data-mp="1x4fyzi1yj01q">
              <p className="text-[10px] uppercase mb-2" style={{
              color: 'rgba(255,255,255,0.65)',
              letterSpacing: '0.3em'
            }} data-id="element-3222" data-mp="3rgwb2zhfp8ei">
                Value
              </p>
              <p className="text-3xl font-serif font-bold tracking-wide" style={{
              color: '#ffffff',
              textShadow: '0 1px 3px rgba(0,0,0,0.3)'
            }} data-id="element-3223" data-mp="1pmhzm6dhp5c8">
                £280
              </p>
            </div>
          </div>

          {/* Bottom */}
          <div className="mt-auto" data-id="element-3224" data-mp="1umnppi442js7">
            <p className="text-xs" style={{
            color: 'rgba(255,255,255,0.65)',
            letterSpacing: '0.2em',
            textShadow: '0 1px 2px rgba(0,0,0,0.3)'
          }} data-id="element-3225" data-mp="28kubhzvsctbi">
              denticoclinic.co.uk
            </p>
          </div>
        </div>
      </div>

      {/* ═══════════════════════════════════════════ */}
      {/* BACK SIDE CARD (Screen Preview + Print)    */}
      {/* ═══════════════════════════════════════════ */}
      <div className="no-print mb-4 text-sm font-semibold text-gray-500 uppercase tracking-wider" data-id="element-3226" data-mp="3ohgl3e8l1do3">
        Back Side
      </div>
      <div className="back-panel overflow-hidden relative print-panel" style={{
      backgroundColor: '#fafaf9',
      WebkitPrintColorAdjust: 'exact',
      printColorAdjust: 'exact',
      boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)'
    } as React.CSSProperties} data-id="element-3227" data-mp="3ta0ar9kjnz99">
        {/* Elegant Border */}
        <div className="absolute inset-5 pointer-events-none" style={{
        border: '1px solid rgba(15,60,60,0.10)'
      }} data-id="element-3228" data-mp="3ahpfa0s7z3l6"></div>
        <div className="absolute inset-7 pointer-events-none" style={{
        border: '1px solid rgba(15,60,60,0.05)'
      }} data-id="element-3229" data-mp="pt1apq0pk095"></div>

        {/* Content wrapper */}
        <div className="relative z-10 flex flex-col py-8 px-10 items-center text-center h-full" data-id="element-3230" data-mp="2fa6kh6qewj5i">
          {/* Top Branding */}
          <div className="flex flex-col items-center mb-5 mt-1" data-id="element-3231" data-mp="2abvamw04a8na">
            <img src="https://cdn.magicpatterns.com/uploads/oHJPxkkc7f6GgU5uyo3LQX/ChatGPT_Image_Feb_5,_2026,_03_12_40_PM.png" alt="Dentico Clinic Logo" className="h-8 w-auto object-contain mb-1.5" data-id="element-3232" data-mp="25jbx1z1fjdih" />
            <span className="text-base font-bold tracking-tight leading-none mb-1" style={{
            color: '#111827'
          }} data-id="element-3233" data-mp="1wkgpojmnfxdl">
              Dentico
              <span style={{
              color: '#0d9488'
            }} data-id="element-3234" data-mp="1mn2f30neq2t7">
                Clinic
              </span>
            </span>
            <p className="text-[9px] uppercase leading-tight" style={{
            color: '#6b7280',
            letterSpacing: '0.2em'
          }} data-id="element-3235" data-mp="3gyh4thgf428y">
              Private Dentistry
            </p>
          </div>

          {/* Title */}
          <h1 className="text-xl font-serif mb-4 tracking-wide" style={{
          color: '#134e4a'
        }} data-id="element-3236" data-mp="udndmblovmea">
            Gift Voucher
          </h1>

          <div className="max-w-[88%] space-y-3 mb-5" data-id="element-3237" data-mp="14dnxpzrdu6jj">
            <p className="text-[13px] leading-relaxed" style={{
            color: '#4b5563'
          }} data-id="element-3238" data-mp="gmiklscttscx">
              Congratulations! You have received a complimentary professional
              teeth whitening experience at Dentico Dental Clinic.
            </p>
            <p className="text-[13px] leading-relaxed" style={{
            color: '#4b5563'
          }} data-id="element-3239" data-mp="lllecgncwc5l">
              We look forward to welcoming you to our clinic and helping you
              achieve a brighter, confident smile.
            </p>
          </div>

          {/* What's Included */}
          <div className="mb-5 w-full max-w-[85%]" data-id="element-3240" data-mp="24igdnx9jqgh8">
            <h3 className="text-[10px] font-bold uppercase tracking-widest mb-3" style={{
            color: '#115e59'
          }} data-id="element-3241" data-mp="29cb5xp37w3rx">
              What's Included
            </h3>
            <ul className="space-y-2 text-left inline-block" data-id="element-3242" data-mp="1zlpydc5kfowv">
              {['Professional clinical teeth whitening', 'Consultation with our dentist', 'Personalised treatment approach'].map((item, idx) => <li key={idx} className="flex items-start gap-2.5" data-id="element-3243" data-mp="2e9b31ai39w6w" data-mpi={idx}>
                  <div className="mt-0.5 w-3.5 h-3.5 rounded-full flex items-center justify-center flex-shrink-0" style={{
                backgroundColor: '#ccfbf1',
                WebkitPrintColorAdjust: 'exact',
                printColorAdjust: 'exact'
              } as React.CSSProperties} data-id="element-3244" data-mp="2ibo7zwp0hq2m">
                    <Check className="w-2 h-2" style={{
                  color: '#0f766e'
                }} data-id="element-3245" data-mp="34lo4iko5zzog" />
                  </div>
                  <span className="text-[13px]" style={{
                color: '#374151'
              }} data-id="element-3246" data-mp="2uu0i4y6gt8tu">
                    {item}
                  </span>
                </li>)}
            </ul>
          </div>

          {/* Unique Code & Validity */}
          <div className="w-full max-w-[180px] mb-5" data-id="element-3247" data-mp="2bvxq0aimevrt">
            <div className="mb-3" data-id="element-3248" data-mp="2bx8u6bfza5jg">
              <label className="block text-[9px] uppercase tracking-widest mb-1" style={{
              color: '#6b7280'
            }} data-id="element-3249" data-mp="f3fefhc8xuu4">
                Unique Code
              </label>
              <div contentEditable suppressContentEditableWarning className="w-full text-sm font-mono text-center pb-1 min-h-[1.5em] focus:outline-none" style={{
              color: '#374151',
              borderBottom: '1px solid #d1d5db'
            }} data-id="element-3250" data-mp="ika992o49fxp">
                DC-RAFFLE-01
              </div>
            </div>
            <p className="text-[10px] text-center tracking-wider" style={{
            color: '#6b7280'
          }} data-id="element-3251" data-mp="3vtg0cnqakxoi">
              Valid until: 30 June 2026
            </p>
          </div>

          {/* Footer Section */}
          <div className="mt-auto w-full flex flex-col items-center" data-id="element-3252" data-mp="dmicnvcj15n6">
            <p className="text-[9px] uppercase tracking-wider mb-3" style={{
            color: '#9ca3af'
          }} data-id="element-3253" data-mp="1k2kpwn95v6jx">
              By appointment only • Non-transferable
            </p>

            {/* Address + Contact Combined */}
            <div className="flex flex-col items-center text-[10px] mb-4 leading-relaxed" style={{
            color: '#4b5563'
          }} data-id="element-3254" data-mp="2y06tmcb1rywy">
              <div className="flex items-center gap-1.5 mb-0.5" data-id="element-3255" data-mp="36wnxqvkzuwj7">
                <MapPin className="w-2.5 h-2.5" style={{
                color: '#0d9488'
              }} data-id="element-3256" data-mp="31z5v1mxkldjn" />
                <span className="font-medium" style={{
                color: '#374151'
              }} data-id="element-3257" data-mp="3bymzk1135le1">
                  Central London – Angel
                </span>
              </div>
              <span data-id="element-3258" data-mp="3lq18t0iu9zkd">360–364 City Road, London EC1V 2PY</span>
              <div className="flex items-center justify-center gap-4 mt-1.5" data-id="element-3259" data-mp="3jd4vdziziga">
                <div className="flex items-center gap-1" data-id="element-3260" data-mp="3up4rw4i0aeyv">
                  <Phone className="w-2.5 h-2.5" style={{
                  color: '#0d9488'
                }} data-id="element-3261" data-mp="3umia7c1eij2x" />
                  <span data-id="element-3262" data-mp="n3lga9305wbg">+44 7345 197820</span>
                </div>
                <div className="flex items-center gap-1" data-id="element-3263" data-mp="87ma53zge68y">
                  <Instagram className="w-2.5 h-2.5" style={{
                  color: '#0d9488'
                }} data-id="element-3264" data-mp="8973iqa17j58" />
                  <span data-id="element-3265" data-mp="3vilubz5er4g6">@dentico_clinic</span>
                </div>
              </div>
            </div>

            {/* Two QR Codes Row */}
            <div className="flex items-start justify-center gap-8 w-full" data-id="element-3266" data-mp="3lm963gsmtist">
              {/* QR 1: Instagram */}
              <div className="flex flex-col items-center" data-id="element-3267" data-mp="3qk0ysd13p8y2">
                <div className="w-16 h-16 rounded-md overflow-hidden p-1" style={{
                border: '1px solid #99f6e4',
                backgroundColor: '#ffffff'
              }} data-id="element-3268" data-mp="3qihdg80l87pf">
                  <img src="https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=https://instagram.com/dentico_clinic&format=png&color=0d4f4f&bgcolor=ffffff" alt="Instagram QR Code" className="w-full h-full object-contain" data-id="element-3269" data-mp="1n4p5kl772slk" />
                </div>
                <div className="mt-1 flex items-center gap-1" data-id="element-3270" data-mp="18cy80i79p4ko">
                  <Instagram className="w-2.5 h-2.5" style={{
                  color: '#0d9488'
                }} data-id="element-3271" data-mp="261gnorb031o1" />
                  <p className="text-[7px] uppercase tracking-wider leading-tight" style={{
                  color: '#6b7280'
                }} data-id="element-3272" data-mp="1w3u25lz74bv3">
                    Follow us
                  </p>
                </div>
              </div>

              {/* QR 2: WhatsApp */}
              <div className="flex flex-col items-center" data-id="element-3273" data-mp="gpy2caulmvod">
                <div className="w-16 h-16 rounded-md overflow-hidden p-1" style={{
                border: '1px solid #99f6e4',
                backgroundColor: '#ffffff'
              }} data-id="element-3274" data-mp="2sc87m2tlk2p2">
                  <img src="https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=https://wa.me/447345197820&format=png&color=0d4f4f&bgcolor=ffffff" alt="WhatsApp QR Code" className="w-full h-full object-contain" data-id="element-3275" data-mp="362les2ynzddc" />
                </div>
                <div className="mt-1 flex items-center gap-1" data-id="element-3276" data-mp="3k1uyhcm2wpm2">
                  <Phone className="w-2.5 h-2.5" style={{
                  color: '#0d9488'
                }} data-id="element-3277" data-mp="3k0jsd655v5rs" />
                  <p className="text-[7px] uppercase tracking-wider leading-tight" style={{
                  color: '#6b7280'
                }} data-id="element-3278" data-mp="1pq6e2ux2vuua">
                    via WhatsApp
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ═══════════════════════════════════════════ */}
      {/* BOTH SIDES ON ONE PAGE (A4 Landscape Preview) */}
      {/* ═══════════════════════════════════════════ */}
      <div className="no-print mb-4 mt-6 text-sm font-semibold text-gray-500 uppercase tracking-wider" data-id="element-3279" data-mp="3dlunrv39da1v">
        Both Sides — One Page
      </div>
      <div className="single-panel overflow-hidden relative print-panel-single flex" style={{
      backgroundColor: '#ffffff',
      WebkitPrintColorAdjust: 'exact',
      printColorAdjust: 'exact',
      boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)'
    } as React.CSSProperties} data-id="element-3280" data-mp="39m3t3gam29s2">
        {/* Fold line */}
        <div className="absolute left-1/2 top-0 bottom-0 w-px border-l border-dashed z-50" style={{
        borderColor: 'rgba(200,200,200,0.4)'
      }} data-id="element-3281" data-mp="xu6ty8jjjxna"></div>

        {/* LEFT — Front Side */}
        <div className="w-1/2 h-full relative flex flex-col items-center justify-center text-center" style={{
        WebkitPrintColorAdjust: 'exact',
        printColorAdjust: 'exact'
      } as React.CSSProperties} data-id="element-3282" data-mp="2kplitidtyrvm">
          <img src="https://images.unsplash.com/photo-1606811841689-23dfddce3e95?auto=format&fit=crop&w=1200&q=80" alt="Dentist and patient smiling" className="absolute inset-0 w-full h-full object-cover z-0" data-id="element-3283" data-mp="2zhdv1libvpt4" />
          <div className="absolute inset-0 z-[1]" style={{
          background: 'linear-gradient(to bottom, rgba(0,0,0,0.50), rgba(0,0,0,0.35), rgba(0,0,0,0.55))',
          WebkitPrintColorAdjust: 'exact',
          printColorAdjust: 'exact'
        } as React.CSSProperties} data-id="element-3284" data-mp="2up2klopyolmb"></div>
          <div className="absolute inset-4 border z-[2] pointer-events-none" style={{
          borderColor: 'rgba(255,255,255,0.25)'
        }} data-id="element-3285" data-mp="c9w8n6rk4mm1"></div>
          <div className="absolute inset-6 border z-[2] pointer-events-none" style={{
          borderColor: 'rgba(255,255,255,0.10)'
        }} data-id="element-3286" data-mp="2j1lm06hlizj"></div>

          <div className="relative z-[3] flex flex-col items-center w-full h-full justify-between py-8 px-8" data-id="element-3287" data-mp="3pnzjjtzsgunn">
            <div className="flex flex-col items-center" data-id="element-3288" data-mp="3bp9osl0fz72g">
              <div className="rounded-xl p-2.5 mb-3" style={{
              backgroundColor: 'rgba(255,255,255,0.95)'
            }} data-id="element-3289" data-mp="3bquk4cukyu1d">
                <img src="https://cdn.magicpatterns.com/uploads/oHJPxkkc7f6GgU5uyo3LQX/ChatGPT_Image_Feb_5,_2026,_03_12_40_PM.png" alt="Dentico Clinic Logo" className="h-10 w-auto object-contain" data-id="element-3290" data-mp="19v0mq9ugaej0" />
              </div>
              <span className="text-xl font-bold tracking-tight leading-none mb-1.5" style={{
              color: '#ffffff',
              textShadow: '0 1px 3px rgba(0,0,0,0.3)'
            }} data-id="element-3291" data-mp="2uh0ev3xsc4x3">
                DenticoClinic
              </span>
              <p className="text-xs uppercase leading-tight" style={{
              color: 'rgba(255,255,255,0.85)',
              letterSpacing: '0.3em',
              textShadow: '0 1px 2px rgba(0,0,0,0.3)'
            }} data-id="element-3292" data-mp="3i2x70vfvotvc">
                Private Dentistry
              </p>
            </div>
            <div className="flex flex-col items-center w-full" data-id="element-3293" data-mp="1wqlzlge7n5t1">
              <div className="w-10 h-px mb-5" style={{
              backgroundColor: 'rgba(255,255,255,0.4)'
            }} data-id="element-3294" data-mp="1rsc22z50jt49"></div>
              <p className="text-[10px] uppercase font-medium mb-4" style={{
              color: 'rgba(255,255,255,0.75)',
              letterSpacing: '0.4em',
              textShadow: '0 1px 2px rgba(0,0,0,0.3)'
            }} data-id="element-3295" data-mp="3rwwp3rixuagu">
                Gift Voucher
              </p>
              <h2 className="text-3xl font-serif mb-6 leading-tight" style={{
              color: '#ffffff',
              textShadow: '0 1px 4px rgba(0,0,0,0.3)'
            }} data-id="element-3296" data-mp="eotl03aysb0o">
                Professional
                <br data-id="element-3297" data-mp="tcy5nctkuwm3" />
                Teeth Whitening
              </h2>
              <div className="inline-block py-3 px-10" style={{
              borderTop: '1px solid rgba(255,255,255,0.35)',
              borderBottom: '1px solid rgba(255,255,255,0.35)'
            }} data-id="element-3298" data-mp="tc50uhe6ih6l">
                <p className="text-[9px] uppercase mb-1.5" style={{
                color: 'rgba(255,255,255,0.65)',
                letterSpacing: '0.3em'
              }} data-id="element-3299" data-mp="2mwq1edt9u67q">
                  Value
                </p>
                <p className="text-2xl font-serif font-bold tracking-wide" style={{
                color: '#ffffff',
                textShadow: '0 1px 3px rgba(0,0,0,0.3)'
              }} data-id="element-3300" data-mp="28wnoz73qg4zv">
                  £280
                </p>
              </div>
            </div>
            <div className="mt-auto" data-id="element-3301" data-mp="2ws9u6rcjgos9">
              <p className="text-[10px]" style={{
              color: 'rgba(255,255,255,0.65)',
              letterSpacing: '0.2em',
              textShadow: '0 1px 2px rgba(0,0,0,0.3)'
            }} data-id="element-3302" data-mp="2ruroraxqcrp0">
                denticoclinic.co.uk
              </p>
            </div>
          </div>
        </div>

        {/* RIGHT — Back Side */}
        <div className="w-1/2 h-full relative flex flex-col py-6 px-8 items-center text-center" style={{
        backgroundColor: '#fafaf9',
        WebkitPrintColorAdjust: 'exact',
        printColorAdjust: 'exact'
      } as React.CSSProperties} data-id="element-3303" data-mp="392r7zuat02ha">
          <div className="absolute inset-4 pointer-events-none" style={{
          border: '1px solid rgba(15,60,60,0.10)'
        }} data-id="element-3304" data-mp="3nsygbh73k549"></div>
          <div className="absolute inset-6 pointer-events-none" style={{
          border: '1px solid rgba(15,60,60,0.05)'
        }} data-id="element-3305" data-mp="3e2lyvxukws2c"></div>

          <div className="relative z-10 flex flex-col items-center h-full w-full" data-id="element-3306" data-mp="alnaxco0ojil">
            {/* Logo */}
            <div className="flex flex-col items-center mb-4 mt-1" data-id="element-3307" data-mp="fclt6hskfroh">
              <img src="https://cdn.magicpatterns.com/uploads/oHJPxkkc7f6GgU5uyo3LQX/ChatGPT_Image_Feb_5,_2026,_03_12_40_PM.png" alt="Dentico Clinic Logo" className="h-7 w-auto object-contain mb-1" data-id="element-3308" data-mp="fdww6fe6svej" />
              <span className="text-sm font-bold tracking-tight leading-none mb-0.5" style={{
              color: '#111827'
            }} data-id="element-3309" data-mp="yfdvr23sjmfg">
                Dentico
                <span style={{
                color: '#0d9488'
              }} data-id="element-3310" data-mp="5sbahbo4l875">
                  Clinic
                </span>
              </span>
              <p className="text-[8px] uppercase leading-tight" style={{
              color: '#6b7280',
              letterSpacing: '0.2em'
            }} data-id="element-3311" data-mp="frimnvj4iwso">
                Private Dentistry
              </p>
            </div>

            <h1 className="text-lg font-serif mb-3 tracking-wide" style={{
            color: '#134e4a'
          }} data-id="element-3312" data-mp="1m5rpcx20xtc5">
              Gift Voucher
            </h1>

            <div className="max-w-[90%] space-y-2 mb-4" data-id="element-3313" data-mp="1y6recfyef5lb">
              <p className="text-[11px] leading-relaxed" style={{
              color: '#4b5563'
            }} data-id="element-3314" data-mp="237e1lwjlhwxp">
                Congratulations! You have received a complimentary professional
                teeth whitening experience at Dentico Dental Clinic.
              </p>
              <p className="text-[11px] leading-relaxed" style={{
              color: '#4b5563'
            }} data-id="element-3315" data-mp="1ficmxrk65pjz">
                We look forward to welcoming you to our clinic and helping you
                achieve a brighter, confident smile.
              </p>
            </div>

            <div className="mb-4 w-full max-w-[85%]" data-id="element-3316" data-mp="wrldyr43g164">
              <h3 className="text-[9px] font-bold uppercase tracking-widest mb-2" style={{
              color: '#115e59'
            }} data-id="element-3317" data-mp="wq0npdotb4e2">
                What's Included
              </h3>
              <ul className="space-y-1.5 text-left inline-block" data-id="element-3318" data-mp="f9mo99jcaq2x">
                {['Professional clinical teeth whitening', 'Consultation with our dentist', 'Personalised treatment approach'].map((item, idx) => <li key={idx} className="flex items-start gap-2" data-id="element-3319" data-mp="1ihv8qah8w1k" data-mpi={idx}>
                    <div className="mt-0.5 w-3 h-3 rounded-full flex items-center justify-center flex-shrink-0" style={{
                  backgroundColor: '#ccfbf1',
                  WebkitPrintColorAdjust: 'exact',
                  printColorAdjust: 'exact'
                } as React.CSSProperties} data-id="element-3320" data-mp="1h6rurky1qaz">
                      <Check className="w-1.5 h-1.5" style={{
                    color: '#0f766e'
                  }} data-id="element-3321" data-mp="16fsc4i9ltl3r" />
                    </div>
                    <span className="text-[11px]" style={{
                  color: '#374151'
                }} data-id="element-3322" data-mp="whcio8n3tzr3">
                      {item}
                    </span>
                  </li>)}
              </ul>
            </div>

            <div className="flex items-end justify-center gap-6 mb-3" data-id="element-3323" data-mp="sin7mmpjguam">
              <div data-id="element-3324" data-mp="dsz4dfxsrlk9">
                <label className="block text-[8px] uppercase tracking-widest mb-0.5" style={{
                color: '#6b7280'
              }} data-id="element-3325" data-mp="dthad4a9he5d">
                  Unique Code
                </label>
                <div contentEditable suppressContentEditableWarning className="text-xs font-mono text-center pb-0.5 min-h-[1.3em] focus:outline-none" style={{
                color: '#374151',
                borderBottom: '1px solid #d1d5db',
                minWidth: '70px'
              }} data-id="element-3326" data-mp="8stcpzrj1z26">
                  DC-RAFFLE-01
                </div>
              </div>
              <p className="text-[9px] tracking-wider pb-0.5" style={{
              color: '#6b7280'
            }} data-id="element-3327" data-mp="92vde9erx1mg">
                Valid until: 30 June 2026
              </p>
            </div>

            <p className="text-[8px] uppercase tracking-wider mb-3" style={{
            color: '#9ca3af'
          }} data-id="element-3328" data-mp="huxd25h0qdvo">
              By appointment only • Non-transferable
            </p>

            {/* Footer */}
            <div className="mt-auto w-full flex flex-col items-center" data-id="element-3329" data-mp="mvb9srd661ee">
              <div className="flex flex-col items-center text-[9px] mb-3 leading-relaxed" style={{
              color: '#4b5563'
            }} data-id="element-3330" data-mp="fbmk4u8o9f4w">
                <div className="flex items-center gap-1 mb-0.5" data-id="element-3331" data-mp="6aogwe625bnd">
                  <MapPin className="w-2 h-2" style={{
                  color: '#0d9488'
                }} data-id="element-3332" data-mp="693oov77u4so" />
                  <span className="font-medium" style={{
                  color: '#374151'
                }} data-id="element-3333" data-mp="3siy4q60bp7b5">
                    Central London – Angel
                  </span>
                </div>
                <span data-id="element-3334" data-mp="3er9s81m5wgtn">360–364 City Road, London EC1V 2PY</span>
                <div className="flex items-center justify-center gap-3 mt-1" data-id="element-3335" data-mp="3eox23169305i">
                  <div className="flex items-center gap-1" data-id="element-3336" data-mp="39qvkkkoh38w5">
                    <Phone className="w-2 h-2" style={{
                    color: '#0d9488'
                  }} data-id="element-3337" data-mp="34sahmdhu6b40" />
                    <span data-id="element-3338" data-mp="2zn7134ceztjo">+44 7345 197820</span>
                  </div>
                  <div className="flex items-center gap-1" data-id="element-3339" data-mp="2zpjjfim345x9">
                    <Instagram className="w-2 h-2" style={{
                    color: '#0d9488'
                  }} data-id="element-3340" data-mp="34o4hpchs0fj2" />
                    <span data-id="element-3341" data-mp="2bzgrr3m311z6">@dentico_clinic</span>
                  </div>
                </div>
              </div>

              <div className="flex items-start justify-center gap-6" data-id="element-3342" data-mp="2gxqmjd3wa0k9">
                <div className="flex flex-col items-center" data-id="element-3343" data-mp="2lw1vllmd871f">
                  <div className="w-12 h-12 rounded overflow-hidden p-0.5" style={{
                  border: '1px solid #99f6e4',
                  backgroundColor: '#ffffff'
                }} data-id="element-3344" data-mp="2luh5q9azxc9w">
                    <img src="https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=https://instagram.com/dentico_clinic&format=png&color=0d4f4f&bgcolor=ffffff" alt="Instagram QR" className="w-full h-full object-contain" data-id="element-3345" data-mp="1y566wxd7hrye" />
                  </div>
                  <div className="mt-0.5 flex items-center gap-0.5" data-id="element-3346" data-mp="1ux1ivvz9np97">
                    <Instagram className="w-2 h-2" style={{
                    color: '#0d9488'
                  }} data-id="element-3347" data-mp="1uyurvhxcqlkd" />
                    <p className="text-[6px] uppercase tracking-wider" style={{
                    color: '#6b7280'
                  }} data-id="element-3348" data-mp="129x7985o4cog">
                      Follow us
                    </p>
                  </div>
                </div>
                <div className="flex flex-col items-center" data-id="element-3349" data-mp="2smmmq58xtaos">
                  <div className="w-12 h-12 rounded overflow-hidden p-0.5" style={{
                  border: '1px solid #99f6e4',
                  backgroundColor: '#ffffff'
                }} data-id="element-3350" data-mp="2nknkiyoxojtf">
                    <img src="https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=https://wa.me/447345197820&format=png&color=0d4f4f&bgcolor=ffffff" alt="WhatsApp QR" className="w-full h-full object-contain" data-id="element-3351" data-mp="2m4rwjoxm6v7w" />
                  </div>
                  <div className="mt-0.5 flex items-center gap-0.5" data-id="element-3352" data-mp="3it7ofro75go6">
                    <Phone className="w-2 h-2" style={{
                    color: '#0d9488'
                  }} data-id="element-3353" data-mp="353nqcz5k97l7" />
                    <p className="text-[6px] uppercase tracking-wider" style={{
                    color: '#6b7280'
                  }} data-id="element-3354" data-mp="p63d0x851hm">
                      WhatsApp
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ═══════════════════════════════════════════ */}
      {/* PRINT STYLES                               */}
      {/* ═══════════════════════════════════════════ */}
      <style data-id="element-3355" data-mp="1zyqricvqd70g">{`
        /* ── Screen preview ── */
        @media screen {
          .print-panel {
            width: 100%;
            max-width: 148.5mm;
            aspect-ratio: 148.5 / 210;
          }
          .print-panel-single {
            width: 100%;
            max-width: 297mm;
            aspect-ratio: 297 / 210;
          }
        }

        /* ── Print: shared rules ── */
        @media print {
          * {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            color-adjust: exact !important;
          }

          html, body {
            margin: 0 !important;
            padding: 0 !important;
            background: white !important;
          }

          .no-print {
            display: none !important;
          }

          img {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }

          svg {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }

          .print-panel {
            box-shadow: none !important;
            margin: 0 !important;
            border-radius: 0 !important;
          }
        }

        /* ── Print FRONT only ── */
        @media print {
          .print-mode-front .back-panel {
            display: none !important;
          }
          .print-mode-front .front-panel {
            width: 148mm !important;
            height: 210mm !important;
            max-width: none !important;
            aspect-ratio: auto !important;
            page-break-after: avoid !important;
          }
          .print-mode-front {
            padding: 0 !important;
            margin: 0 !important;
          }
        }

        /* ── Print BACK only ── */
        @media print {
          .print-mode-back .front-panel {
            display: none !important;
          }
          .print-mode-back .back-panel {
            width: 148mm !important;
            height: 210mm !important;
            max-width: none !important;
            aspect-ratio: auto !important;
            page-break-after: avoid !important;
          }
          .print-mode-back {
            padding: 0 !important;
            margin: 0 !important;
          }
        }

        /* ── Print BOTH (A4 Landscape, side by side) ── */
        @media print {
          .print-mode-both {
            padding: 0 !important;
            margin: 0 !important;
          }
          .print-mode-both .front-panel,
          .print-mode-both .back-panel {
            width: 148.5mm !important;
            height: 210mm !important;
            max-width: none !important;
            aspect-ratio: auto !important;
            display: inline-block !important;
            vertical-align: top !important;
            float: left !important;
          }
          .print-mode-both .single-panel {
            display: none !important;
          }
        }

        /* ── Print SINGLE (A4 Landscape, both sides on one page) ── */
        @media print {
          .print-mode-single .front-panel,
          .print-mode-single .back-panel {
            display: none !important;
          }
          .print-mode-single .single-panel {
            width: 297mm !important;
            height: 210mm !important;
            max-width: none !important;
            aspect-ratio: auto !important;
            page-break-after: avoid !important;
            box-shadow: none !important;
            border-radius: 0 !important;
            margin: 0 !important;
            display: flex !important;
          }
          .print-mode-single {
            padding: 0 !important;
            margin: 0 !important;
          }
        }

        /* ── Hide single panel in front/back modes ── */
        @media print {
          .print-mode-front .single-panel,
          .print-mode-back .single-panel {
            display: none !important;
          }
        }

        /* ── @page rules per mode ── */
        .print-mode-front ~ style,
        .print-mode-back ~ style {
          /* Handled by dynamic @page below */
        }
      `}</style>

      {/* Dynamic @page size based on print mode */}
      {printMode === 'front' || printMode === 'back' ? <style data-id="element-3356" data-mp="ag3mjohf7hg4">{`
          @media print {
            @page {
              size: 148mm 210mm;
              margin: 0;
            }
          }
        `}</style> : printMode === 'both' ? <style data-id="element-3357" data-mp="24t38s0gb39wd">{`
          @media print {
            @page {
              size: A4 landscape;
              margin: 0;
            }
          }
        `}</style> : printMode === 'single' ? <style data-id="element-3358" data-mp="27e0x0mt2hbnw">{`
          @media print {
            @page {
              size: A4 landscape;
              margin: 0;
            }
          }
        `}</style> : null}
    </div>;
}